import { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, TextField, InputAdornment, Autocomplete, createFilterOptions } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { SCUIGeneralDrawer, SCUIGeneralDrawerHeader } from '../../../atomic/molecules';
import { useTranslation } from 'react-i18next';
import { SCUIButton, SCUILoadingLinear } from '../../../atomic/atoms';
import PostCheckoutQuery from '../../../services/query/postCheckout/PostCheckoutQuery.service';
import CargoManagementQuery from '../../../services/query/cargoManagement/CargoManagementQuery.services';
import CargoCompanyDistributionAreasQuery from '../../../services/query/cargoCompanyDistributionAreas/CargoCompanyDistributionAreasQuery.services';
import { CCDA, ccdaCls } from '../cargoCompanyDistributionAreas.classes';

// Types
interface SearchDrawerProps {
    isDrawerOpen: boolean;
    handleDrawerClose: () => void;
    handleSearch?: (filterParams: any) => void;
}

interface Option {
    id: number;
    name: string;
}

interface FormState {
    selectedCargoCompany: Option | null;
    selectedCity: Option | null;
    selectedCounty: Option | null;
    selectedDistributionType: Option | null;
}

// Constants
const DRAWER_WIDTH = '480px';
const ADDRESS_ENTITY_TYPE_CITY = 3;
const ADDRESS_ENTITY_TYPE_COUNTY = 4;
const ALL_OPTION_ID = -1;

// Helper function to get countryId from localStorage
const getCountryId = (): number => {
    try {
        const countryId = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId;
        return countryId ? Number(countryId) : 1;
    } catch {
        return 1;
    }
};

// Helper to build address entity request params
const buildAddressEntityParams = (addressEntityTypeId: number, parentAddressEntityId = 0) => ({
    countryId: getCountryId(),
    cultureCode: localStorage.getItem('i18nextLng') || 'tr-TR',
    parentAddressEntityId,
    deliveryMethodId: 0,
    addressEntityTypeId,
    isVisible: true,
    isMpStore: true,
});

// Helper to extract address entities from response
const extractAddressEntities = (res: any): Option[] => {
    const addressEntities = res?.data?.addressEntities || res?.addressEntities || [];
    if (Array.isArray(addressEntities)) {
        return addressEntities.map((item: any) => ({
            id: item?.addressEntityId ?? item?.id,
            name: item?.name ?? '',
        }));
    }
    return [];
};

// Helper to transform cargo companies to options
const transformCargoCompaniesToOptions = (companies: any[]): Option[] => {
    if (!Array.isArray(companies)) return [];
    return companies
        .filter(
            (company: any) =>
                company?.shippingCompanyId != null && company?.name != null && company?.isCargoCompany === true
        )
        .map((company: any) => ({
            id: company.shippingCompanyId,
            name: company.name ?? '',
        }));
};

// Helper to transform distribution types to options
const transformDistributionTypesToOptions = (types: any[]): Option[] => {
    if (!Array.isArray(types)) return [];
    return types.map((item: any) => ({
        id: item?.id ?? 0,
        name: item?.name ?? '',
    }));
};

// Initial form state
const INITIAL_FORM_STATE: FormState = {
    selectedCargoCompany: null,
    selectedCity: null,
    selectedCounty: null,
    selectedDistributionType: null,
};

// Create filter options
const filterOptions = createFilterOptions<Option>({
    matchFrom: 'any',
    stringify: (option) => option.name,
});

export const SearchDrawer = ({ isDrawerOpen, handleDrawerClose, handleSearch }: SearchDrawerProps) => {
    const { t } = useTranslation();

    const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE);
    const [cargoCompanyOptions, setCargoCompanyOptions] = useState<Option[]>([]);
    const [cityOptions, setCityOptions] = useState<Option[]>([]);
    const [countyOptions, setCountyOptions] = useState<Option[]>([]);
    const [distributionTypeOptions, setDistributionTypeOptions] = useState<Option[]>([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);
    const [isLoadingCounties, setIsLoadingCounties] = useState(false);

    // "All" option for city, county, and distributionType
    const allOption: Option = useMemo(() => ({ id: ALL_OPTION_ID, name: t('general.all') ?? 'Tümü' }), [t]);

    // City options with "All"
    const cityOptionsWithAll = useMemo(() => [allOption, ...cityOptions], [allOption, cityOptions]);

    // County options with "All"
    const countyOptionsWithAll = useMemo(() => [allOption, ...countyOptions], [allOption, countyOptions]);

    // DistributionType options with "All"
    const distributionTypeOptionsWithAll = useMemo(
        () => [allOption, ...distributionTypeOptions],
        [allOption, distributionTypeOptions]
    );

    // Fetch cargo companies on drawer open
    useEffect(() => {
        if (isDrawerOpen && cargoCompanyOptions.length === 0) {
            setIsLoadingOptions(true);
            PostCheckoutQuery.GetShippingCompanies()
                .then((res: any) => {
                    if (res?.shippingCompanies) {
                        const options = transformCargoCompaniesToOptions(res.shippingCompanies);
                        setCargoCompanyOptions(options);
                    }
                })
                .catch((err: any) => {
                    console.error('Error fetching cargo companies:', err);
                })
                .finally(() => {
                    setIsLoadingOptions(false);
                });
        }
    }, [isDrawerOpen, cargoCompanyOptions.length]);

    // Fetch cities on drawer open
    useEffect(() => {
        if (isDrawerOpen && cityOptions.length === 0) {
            setIsLoadingOptions(true);
            const params = buildAddressEntityParams(ADDRESS_ENTITY_TYPE_CITY);

            CargoManagementQuery.SubAddressEntities(params)
                .then((res: any) => {
                    setCityOptions(extractAddressEntities(res));
                })
                .catch((err: any) => {
                    console.error('Error fetching cities:', err);
                })
                .finally(() => {
                    setIsLoadingOptions(false);
                });
        }
    }, [isDrawerOpen, cityOptions.length]);

    // Fetch distribution types on drawer open
    useEffect(() => {
        if (isDrawerOpen && distributionTypeOptions.length === 0) {
            setIsLoadingOptions(true);
            CargoCompanyDistributionAreasQuery.GetServiceDistributionTypes()
                .then((res: any) => {
                    const serviceDistributionTypes = res?.serviceDistributionTypes || res?.data?.serviceDistributionTypes || [];
                    const options = transformDistributionTypesToOptions(serviceDistributionTypes);
                    setDistributionTypeOptions(options);
                })
                .catch((err: any) => {
                    console.error('Error fetching distribution types:', err);
                })
                .finally(() => {
                    setIsLoadingOptions(false);
                });
        }
    }, [isDrawerOpen, distributionTypeOptions.length]);

    // Fetch counties when city changes
    useEffect(() => {
        if (formState.selectedCity && formState.selectedCity.id !== ALL_OPTION_ID) {
            setIsLoadingCounties(true);
            setFormState((prev) => ({ ...prev, selectedCounty: null }));

            const params = buildAddressEntityParams(ADDRESS_ENTITY_TYPE_COUNTY, formState.selectedCity.id);

            CargoManagementQuery.SubAddressEntities(params)
                .then((res: any) => {
                    setCountyOptions(extractAddressEntities(res));
                })
                .catch((err: any) => {
                    console.error('Error fetching counties:', err);
                })
                .finally(() => {
                    setIsLoadingCounties(false);
                });
        } else {
            setCountyOptions([]);
            setFormState((prev) => ({ ...prev, selectedCounty: null }));
        }
    }, [formState.selectedCity]);

    // Reset form when drawer closes
    useEffect(() => {
        if (!isDrawerOpen) {
            setFormState(INITIAL_FORM_STATE);
            setCountyOptions([]);
        }
    }, [isDrawerOpen]);

    // Check if form is valid (cargo company is required)
    const isFormValid = useMemo(() => formState.selectedCargoCompany !== null, [formState.selectedCargoCompany]);

    const handleSubmit = useCallback(() => {
        const filterParams = {
            cargoId: formState.selectedCargoCompany?.id ?? 0,
            cityId: formState.selectedCity?.id ?? ALL_OPTION_ID,
            countyId: formState.selectedCounty?.id ?? ALL_OPTION_ID,
            distributionTypeId: formState.selectedDistributionType?.id ?? ALL_OPTION_ID,
        };
        handleSearch?.(filterParams);
    }, [formState, handleSearch]);

    const handleClear = useCallback(() => {
        setFormState(INITIAL_FORM_STATE);
        setCountyOptions([]);
    }, []);

    // Form field change handlers
    const handleCargoCompanyChange = useCallback((_: any, newValue: Option | null) => {
        setFormState((prev) => ({ ...prev, selectedCargoCompany: newValue }));
    }, []);

    const handleCityChange = useCallback((_: any, newValue: Option | null) => {
        setFormState((prev) => ({ ...prev, selectedCity: newValue, selectedCounty: null }));
    }, []);

    const handleCountyChange = useCallback((_: any, newValue: Option | null) => {
        setFormState((prev) => ({ ...prev, selectedCounty: newValue }));
    }, []);

    const handleDistributionTypeChange = useCallback((_: any, newValue: Option | null) => {
        setFormState((prev) => ({ ...prev, selectedDistributionType: newValue }));
    }, []);

    // Memoized option equality checker
    const isOptionEqualToValue = useCallback((option: Option, value: Option) => option.id === value.id, []);

    // Memoized option label getter
    const getOptionLabel = useCallback((option: Option) => option.name, []);

    // Memoized common autocomplete styles
    const autocompleteSx = useMemo(
        () => ({
            '& .MuiAutocomplete-inputRoot': {
                minHeight: '50px',
            },
        }),
        []
    );

    // Memoized search icon adornment
    const searchIconAdornment = useMemo(
        () => (
            <InputAdornment position="start">
                <SearchIcon fontSize="small" />
            </InputAdornment>
        ),
        []
    );

    const renderSearchInput = (params: any, label: string, placeholder: string, fieldClass: string) => (
        <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            className={ccdaCls(CCDA.input, CCDA.inputTextfield, CCDA.inputAutocomplete, fieldClass, params.className)}
            slotProps={{
                input: {
                    ...params.InputProps,
                    startAdornment: (
                        <>
                            {searchIconAdornment}
                            {params.InputProps.startAdornment}
                        </>
                    ),
                },
            }}
        />
    );

    const isButtonsDisabled = isLoadingOptions || isLoadingCounties;
    const showLoadingIndicator = isLoadingOptions || isLoadingCounties;

    return (
        <SCUIGeneralDrawer width={DRAWER_WIDTH} open={isDrawerOpen} onClose={handleDrawerClose}>
            <SCUIGeneralDrawerHeader header={t('cargoCompanyDistributionAreas.searchDrawerTitle')} onClose={handleDrawerClose} />
            {showLoadingIndicator && <SCUILoadingLinear display={showLoadingIndicator} />}
            <Box
                className={ccdaCls(CCDA.drawer, CCDA.drawerBody)}
                sx={{ padding: '16px', display: 'flex', flexDirection: 'column' }}
            >
                {/* Form Container */}
                <Box
                    className={CCDA.drawerForm}
                    sx={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '16px' }}
                >
                    {/* Cargo Company - Required, single select, no "All" option */}
                    <Autocomplete
                        className={ccdaCls(CCDA.input, CCDA.inputAutocomplete, CCDA.fieldCargoCompany)}
                        fullWidth
                        options={cargoCompanyOptions}
                        getOptionLabel={getOptionLabel}
                        value={formState.selectedCargoCompany}
                        onChange={handleCargoCompanyChange}
                        loading={isLoadingOptions}
                        disabled={isButtonsDisabled}
                        isOptionEqualToValue={isOptionEqualToValue}
                        filterOptions={filterOptions}
                        sx={autocompleteSx}
                        renderOption={(props, option) => (
                            <li {...props} key={`cargo-company-${option.id}`}>
                                {option.name}
                            </li>
                        )}
                        renderInput={(params) =>
                            renderSearchInput(
                                params,
                                `${t('cargoCompanyDistributionAreas.cargoCompany')} (*)`,
                                t('general.search') ?? '',
                                CCDA.fieldCargoCompany
                            )
                        }
                    />

                    {/* City - Single select with "All" option */}
                    <Autocomplete
                        className={ccdaCls(CCDA.input, CCDA.inputAutocomplete, CCDA.fieldCity)}
                        fullWidth
                        options={cityOptionsWithAll}
                        getOptionLabel={getOptionLabel}
                        value={formState.selectedCity}
                        onChange={handleCityChange}
                        loading={isLoadingOptions}
                        disabled={isButtonsDisabled}
                        isOptionEqualToValue={isOptionEqualToValue}
                        filterOptions={filterOptions}
                        sx={autocompleteSx}
                        renderOption={(props, option) => (
                            <li {...props} key={`city-${option.id}`}>
                                {option.name}
                            </li>
                        )}
                        renderInput={(params) =>
                            renderSearchInput(
                                params,
                                t('cargoCompanyDistributionAreas.city'),
                                t('general.search') ?? '',
                                CCDA.fieldCity
                            )
                        }
                    />

                    {/* County - Single select with "All" option, disabled if no city selected or city is "All" */}
                    <Autocomplete
                        className={ccdaCls(CCDA.input, CCDA.inputAutocomplete, CCDA.fieldCounty)}
                        fullWidth
                        options={countyOptionsWithAll}
                        getOptionLabel={getOptionLabel}
                        value={formState.selectedCounty}
                        onChange={handleCountyChange}
                        loading={isLoadingCounties}
                        disabled={!formState.selectedCity || formState.selectedCity.id === ALL_OPTION_ID || isButtonsDisabled}
                        isOptionEqualToValue={isOptionEqualToValue}
                        filterOptions={filterOptions}
                        sx={autocompleteSx}
                        renderOption={(props, option) => (
                            <li {...props} key={`county-${option.id}`}>
                                {option.name}
                            </li>
                        )}
                        renderInput={(params) =>
                            renderSearchInput(
                                params,
                                t('cargoCompanyDistributionAreas.county'),
                                t('general.search') ?? '',
                                CCDA.fieldCounty
                            )
                        }
                    />

                    {/* Distribution Type - Single select with "All" option */}
                    <Autocomplete
                        className={ccdaCls(CCDA.input, CCDA.inputAutocomplete, CCDA.fieldDistributionType)}
                        fullWidth
                        options={distributionTypeOptionsWithAll}
                        getOptionLabel={getOptionLabel}
                        value={formState.selectedDistributionType}
                        onChange={handleDistributionTypeChange}
                        loading={isLoadingOptions}
                        disabled={isButtonsDisabled}
                        isOptionEqualToValue={isOptionEqualToValue}
                        filterOptions={filterOptions}
                        sx={autocompleteSx}
                        renderOption={(props, option) => (
                            <li {...props} key={`distribution-type-${option.id}`}>
                                {option.name}
                            </li>
                        )}
                        renderInput={(params) =>
                            renderSearchInput(
                                params,
                                t('cargoCompanyDistributionAreas.distributionType'),
                                t('general.search') ?? '',
                                CCDA.fieldDistributionType
                            )
                        }
                    />
                </Box>

                {/* Buttons */}
                <Box
                    className={CCDA.drawerActions}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '8px',
                        width: '100%',
                        paddingTop: '32px',
                        padding: '12px',
                    }}
                >
                    <SCUIButton
                        text={t('general.clear')}
                        onClick={handleClear}
                        buttonType="button"
                        type="primary"
                        sizeType="fixed"
                        size={'l'}
                        style={{ backgroundColor: 'red', borderColor: 'red' }}
                        disabled={isButtonsDisabled}
                        className={ccdaCls(CCDA.input, CCDA.inputButton, CCDA.btnDrawerClear)}
                    />
                    <SCUIButton
                        text={t('general.search')}
                        type="primary"
                        sizeType="fixed"
                        disabled={!isFormValid || isButtonsDisabled}
                        size={'l'}
                        buttonType="button"
                        onClick={handleSubmit}
                        className={ccdaCls(CCDA.input, CCDA.inputButton, CCDA.btnDrawerSubmit)}
                    />
                </Box>
            </Box>
        </SCUIGeneralDrawer>
    );
};

